import { Form, Formik } from 'formik';
import queryString from 'query-string';
import React, { useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  DataTableColumn,
  DataTableLoopbackFilter,
  DEFAULT_SIZE_PER_PAGE,
  GenericIndexScene,
  Select,
} from 'lib/releox-react';
import OrderColumn from '../../../columns/OrderColumn';
import routes from '../../../configs/routes';
import { teamOptions } from '../../../options';
import { ReduxDispatch } from '../../../store/Action';
import OrdersAction from '../../../store/order/OrdersAction';
import OrderQuery from '../../../store/order/OrdersQuery';
import OrdersSelector from '../../../store/order/OrdersSelector';

export default () => {
  const params = useParams<{ language: string }>();
  const location = useLocation();
  const history = useHistory();

  const indexUrl = routes.BUYER.COLD_CASES_INDEX_ROOT.replace(':language', params.language);

  // List state (team filter, page and page size) is kept in the url so that it
  // survives opening a case and coming back to the list.
  const { team, page, sizePerPage } = useMemo(() => {
    const query = queryString.parse(location.search);
    return {
      team: (query.team as string) || '',
      page: Number(query.page) || 1,
      sizePerPage: Number(query.limit) || DEFAULT_SIZE_PER_PAGE,
    };
  }, [location.search]);

  const getSearch = useCallback(
    (nextTeam: string, nextPage: number, nextLimit: number) =>
      queryString.stringify({ team: nextTeam || undefined, page: nextPage, limit: nextLimit }),
    []
  );

  const columns: DataTableColumn[] = [
    OrderColumn.orderNumber,
    OrderColumn.phone,
    OrderColumn.clientName,
    OrderColumn.coldCallTries,
    OrderColumn.coldCallLatest,
    OrderColumn.createdAt,
    OrderColumn.callerName,
  ];

  const sort = {
    dataField: 'createdAt',
    order: 'DESC',
  };

  const handleSubmit = useCallback(
    (values: { team: string }) => {
      history.replace(`${indexUrl}?${getSearch(values.team, 1, sizePerPage)}`);
    },
    [history, indexUrl, getSearch, sizePerPage]
  );

  const handleChangeLoopback = useCallback(
    (config: DataTableLoopbackFilter) => {
      const limit = config.limit || DEFAULT_SIZE_PER_PAGE;
      const nextPage = Math.floor((config.skip || 0) / limit) + 1;
      const search = getSearch(team, nextPage, limit);

      return (dispatch: ReduxDispatch) => {
        if (`?${search}` !== location.search) history.replace(`${indexUrl}?${search}`);
        return dispatch(OrdersAction.list(config));
      };
    },
    [history, indexUrl, getSearch, team, location.search]
  );

  return (
    <div key={params.language}>
      <Card>
        <Formik initialValues={{ team }} enableReinitialize onSubmit={handleSubmit}>
          <Form>
            {/* <CCManagerCallListSearchForm /> */}
            <Select name="team" label="Team" options={teamOptions} />
            <Button id="search" type="submit">
              Search
            </Button>
          </Form>
        </Formik>
      </Card>
      <div key={`${team}:${params.language}`}>
        <GenericIndexScene
          tNamespace="ColdCasesIndexScene"
          loadingSelector={OrdersSelector.selectIsListLoading}
          dataSelector={OrdersSelector.selectOrders}
          dataSizeSelector={OrdersSelector.selectOrdersCount}
          onChangeLoopback={handleChangeLoopback}
          columns={columns}
          defaultSorted={sort}
          defaultPage={page}
          initialSizePerPage={sizePerPage}
          query={OrderQuery.getColdCaseIndex(params.language, team)}
          redirectUrl={`${routes.BUYER.COLD_CASES_SHOW.replace(
            ':language',
            params.language
          )}?${getSearch(team, page, sizePerPage)}`}
        />
      </div>
    </div>
  );
};
